/**
 * Flavor system for StudyStride student UI.
 *
 * A "flavor" controls the terminology used throughout the student dashboard:
 * what courses are called, what the activity feed is called, what the rank title suffix
 * is, etc. Visual theming (colours, backgrounds) is future work.
 *
 * Default flavor: "military".
 *
 * Preference is stored in localStorage under the key `studystride_flavor`.
 */

export type Flavor = 'military' | 'space' | 'sports' | 'rpg' | 'explorer' | 'neutral';

export interface FlavorLabels {
  /** How individual courses are referred to in headings. */
  coursesLabel: string;
  /** Singular version of the course label. */
  courseLabel: string;
  /** Label for the "Active Courses" section heading. */
  activeCoursesLabel: string;
  /** Label for the activity feed / recent events section. */
  activityFeedLabel: string;
  /** Label for a single mission / task card. */
  missionLabel: string;
  /** Label for an in-progress course item in the hero carousel. */
  operationLabel: string;
  /** Optional text appended to the rank name in the rank display (e.g. "Operator"). */
  rankSuffix: string;
  /** What the "Continue" button says for a course. */
  continueLabel: string;
  /** Label prefix for the hero section greeting. */
  heroGreeting: string;
  /** Small description under the student's rank/name. */
  statusLine: string;
}

export const FLAVOR_LABELS: Record<Flavor, FlavorLabels> = {
  military: {
    coursesLabel: 'Operations',
    courseLabel: 'Operation',
    activeCoursesLabel: 'Active Operations',
    activityFeedLabel: 'Mission Log',
    missionLabel: 'Mission',
    operationLabel: 'Active Briefing',
    rankSuffix: 'Operator',
    continueLabel: 'Resume Mission',
    heroGreeting: 'Welcome back, Cadet',
    statusLine: 'Report for duty. Your operations are waiting.',
  },
  space: {
    coursesLabel: 'Expeditions',
    courseLabel: 'Expedition',
    activeCoursesLabel: 'Active Expeditions',
    activityFeedLabel: 'Mission Log',
    missionLabel: 'Mission',
    operationLabel: 'Active Launch',
    rankSuffix: 'Explorer',
    continueLabel: 'Continue Expedition',
    heroGreeting: 'Welcome back, Cadet',
    statusLine: 'The cosmos awaits. Launch your next expedition.',
  },
  sports: {
    coursesLabel: 'Seasons',
    courseLabel: 'Season',
    activeCoursesLabel: 'Active Seasons',
    activityFeedLabel: 'Match Log',
    missionLabel: 'Challenge',
    operationLabel: 'Active Season',
    rankSuffix: 'Player',
    continueLabel: 'Keep Training',
    heroGreeting: 'Ready to play?',
    statusLine: 'Your season stats are looking good. Keep the streak alive!',
  },
  rpg: {
    coursesLabel: 'Quests',
    courseLabel: 'Quest',
    activeCoursesLabel: 'Active Quests',
    activityFeedLabel: 'Adventure Log',
    missionLabel: 'Quest',
    operationLabel: 'Active Quest',
    rankSuffix: 'Hero',
    continueLabel: 'Continue Quest',
    heroGreeting: 'Hail, Adventurer',
    statusLine: 'New quests await. Your saga continues.',
  },
  explorer: {
    coursesLabel: 'Journeys',
    courseLabel: 'Journey',
    activeCoursesLabel: 'Active Journeys',
    activityFeedLabel: 'Explorer\'s Log',
    missionLabel: 'Expedition',
    operationLabel: 'Active Journey',
    rankSuffix: 'Trailblazer',
    continueLabel: 'Explore Further',
    heroGreeting: 'Hello, Explorer',
    statusLine: 'New territories to discover. Keep exploring!',
  },
  neutral: {
    coursesLabel: 'Courses',
    courseLabel: 'Course',
    activeCoursesLabel: 'My Courses',
    activityFeedLabel: 'Activity Feed',
    missionLabel: 'Task',
    operationLabel: 'Current Course',
    rankSuffix: 'Learner',
    continueLabel: 'Continue',
    heroGreeting: 'Welcome back',
    statusLine: 'Keep up the great work!',
  },
};

export const FLAVOR_STORAGE_KEY = 'studystride_flavor';
export const DEFAULT_FLAVOR: Flavor = 'military';

/**
 * Returns all available flavor options for display in a picker.
 */
export const FLAVOR_OPTIONS: Array<{ value: Flavor; label: string }> = [
  { value: 'military', label: '🎖️ Military' },
  { value: 'space',    label: '🚀 Space' },
  { value: 'sports',   label: '⚽ Sports' },
  { value: 'rpg',      label: '⚔️ RPG' },
  { value: 'explorer', label: '🗺️ Explorer' },
  { value: 'neutral',  label: '📚 Classic' },
];
